import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { quotes, softHolds } from "@fm/api";
import { parseRenterDriverError } from "@fm/api";
import type { PricingBreakdown } from "@fm/api";
import type { CheckoutScreeningQuestion } from "@fm/api/screening-questions-checkout";

import { useVehicleAddons } from "../hooks/useAddons";
import { EMPTY_LIST } from "../lib/stableDefaults";
import {
  isCustomAddressOffered,
  isDeliveryOffered,
  resolveDeliveryMode,
} from "../lib/deliveryAvailability";
import { keepOfferedAddonIds } from "../lib/offered-addon-ids";
import { useDeliveryLocations } from "../hooks/useDeliveryLocations";
import { useVehicle } from "@fm/react/vehicles";
import { resolveVehicleName, vehicleNameDiagnostic } from "../lib/vehicle-name";
import { EmbedProvider, useEmbedContext } from "../contexts/EmbedContext";
import { calculateDeliveryFee } from "../utils/googleMaps";
import {
  CheckoutSummary,
  CustomerInfoForm,
  CheckoutPaymentElementForm,
  Button,
  buildEvidenceForAcceptedDocuments,
  type DocumentViewTimes,
} from "@fm/ui";
import { AddonSelector } from "./AddonSelector";
import { InsuranceOptionSelector } from "./InsuranceOptionSelector";
import { AcceptanceDocumentsSection } from "./AcceptanceDocumentsSection";
import { useAcceptanceDocuments } from "../hooks/useAcceptanceDocuments";
import { hasUnacceptedMandatoryDocuments as computeHasUnacceptedMandatoryDocuments } from "../lib/acceptanceDocuments";
import { buildSoftHoldRequest } from "../lib/buildSoftHoldRequest";
import { getCheckoutCopy, isRequestToBook } from "../lib/bookingRequestCopy";
import {
  buildScreeningPayload,
  validateScreeningAnswers,
  type ScreeningErrors,
  type ScreeningValues,
} from "../lib/screening";
import { parseSoftHoldError } from "../lib/softHoldErrors";
import { extractErrorCode, getDeliveryErrorInfo } from "../lib/deliveryErrors";
import { DeliveryErrorActions } from "./DeliveryErrorActions";
import {
  creditCardRequiredMessage,
  isCreditCardRequiredFailure,
  resolveHoldPoll,
  type RequestOutcome,
} from "../lib/requestStatus";
import { BookingRequestOutcome } from "./BookingRequestOutcome";
import { ScreeningQuestionsSection } from "./ScreeningQuestionsSection";
import {
  RenterDriverCards,
  useRenterDriverCards,
  useRenterDriversConfig,
} from "./renter-drivers";
import { driversRequired as computeDriversRequired } from "../lib/renter-drivers";

import {
  DeliveryOptionsSelector,
  type CustomAddress,
} from "./DeliveryOptionsSelector";
import { PricingBreakdownDisplay } from "./PricingBreakdownDisplay";
import { SecurityDepositBanner } from "./SecurityDepositBanner";
import { DepositConsentCheckbox } from "./DepositConsentCheckbox";
import { DepositCardCapture } from "./deposit/DepositCardCapture";
import { DepositHoldStatus } from "./deposit/DepositHoldStatus";
import { useDepositHoldFlow } from "../hooks/deposit/useDepositHoldFlow";
import { getApiBaseUrl } from "../lib/config";
import { buildBookingHref } from "../utils/bookingPage";
import { useBusinessProfile } from "../hooks/useBusinessProfile";
import { useInsuranceOptions } from "../hooks/useInsuranceOptions";
import { useInsuranceOptionGroups } from "../hooks/useInsuranceOptionGroups";
import { useScreeningQuestionsCheckout } from "../hooks/useScreeningQuestionsCheckout";

// Soft Hold Management (localStorage for iframe compatibility)
const PENDING_SOFT_HOLD_KEY = "pending_soft_hold";

interface PendingSoftHold {
  hold_id: string;
  hold_token: string;
  hold_expires_at: string; // ISO timestamp
  vehicle_id: string;
  start_date: string; // from
  end_date: string; // to
  selected_addon_ids: string[];
  selected_insurance_option_ids: string[];
  total_amount: string;
}

function setPendingSoftHold(data: PendingSoftHold): void {
  try {
    localStorage.setItem(PENDING_SOFT_HOLD_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn("Failed to save pending soft hold:", error);
  }
}

function getPendingSoftHold(): PendingSoftHold | null {
  try {
    const stored = localStorage.getItem(PENDING_SOFT_HOLD_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored) as PendingSoftHold;

    // Check if expired
    if (new Date(data.hold_expires_at) < new Date()) {
      clearPendingSoftHold();
      return null;
    }

    return data;
  } catch (error) {
    console.warn("Failed to get pending soft hold:", error);
    return null;
  }
}

function clearPendingSoftHold(): void {
  try {
    localStorage.removeItem(PENDING_SOFT_HOLD_KEY);
  } catch (error) {
    console.warn("Failed to clear pending soft hold:", error);
  }
}

interface CheckoutContainerProps {
  vehicleId: string;
  vehicleName: string;
  totalPrice: number;
  from: string;
  to: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
  apiKey: string;
  fleetId: string;
  stripePublishableKey: string;
  googleMapsApiKey?: string;
  apiBaseUrl?: string;
  defaultLogoUrl?: string;
  /** Open the header logo link (booking page) in a new tab. */
  openInNewTab?: boolean;
  /** Absolute app origin for the booking-page link (web-component use on
   *  third-party sites; omit inside same-origin iframes). */
  appBaseUrl?: string;
  theme?: "light" | "dark";
  onSuccess?: (bookingId: string) => void;
  onError?: (error: string) => void;
  onNavigateToSuccess?: (bookingId: string) => void;
  onNavigateToError?: (error: string) => void;
  initialSelectedAddonIds?: string[];
  initialSelectedInsuranceOptionIds?: string[];
  initialDeliveryType?: "pickup" | "delivery";
  initialDeliveryOption?: "custom" | "location";
  initialSelectedLocationId?: string;
  initialCustomAddress?: CustomAddress | null;
  initialDeliveryDistance?: number | null;
  initialTaxcloudCartId?: string | null;
}

function CheckoutContainerInner({
  vehicleId,
  vehicleName,
  totalPrice,
  from,
  to,
  adults,
  children,
  infants,
  pets,
  googleMapsApiKey,
  apiBaseUrl,
  defaultLogoUrl,
  openInNewTab,
  appBaseUrl,
  onSuccess,
  onError,
  onNavigateToSuccess,
  onNavigateToError,
  initialSelectedAddonIds,
  initialSelectedInsuranceOptionIds,
  initialDeliveryType,
  initialDeliveryOption,
  initialSelectedLocationId,
  initialCustomAddress,
  initialDeliveryDistance,
  initialTaxcloudCartId,
}: Omit<
  CheckoutContainerProps,
  "apiKey" | "fleetId" | "stripePublishableKey" | "theme"
>) {
  const {
    apiKey,
    fleetId,
    stripePublishableKey,
    externalAccountId,
    businessProfile,
    securityDepositMode,
    acceptCashDeposit,
    configLoading,
    configError,
  } = useEmbedContext();

  // The booking-page slug isn't on the config payload, so fetch the business
  // profile (which includes it) to make the logo link back to /book/{slug}.
  const { profile: fleetProfile } = useBusinessProfile(apiKey, apiBaseUrl);
  const logoHref = buildBookingHref(
    fleetProfile?.slug,
    appBaseUrl,
    fleetProfile?.custom_domain,
  );

  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>(
    initialSelectedAddonIds ?? [],
  );

  // Insurance Options state
  const [selectedInsuranceOptionIds, setSelectedInsuranceOptionIds] = useState<
    string[]
  >(initialSelectedInsuranceOptionIds ?? []);

  // Map of insurance optionId -> accepted terms version (or null)
  const [acceptedInsuranceTerms, setAcceptedInsuranceTerms] = useState<
    Record<string, string | null>
  >({});

  // fleet-agreement-acceptance (path 1): version ids of the vehicle's
  // mandatory documents the customer has explicitly checked. Always starts
  // empty — this must never be pre-populated, regardless of whether the
  // server's `fleet_agreement_acceptance_enforced` flag is on.
  const [acceptedDocumentVersionIds, setAcceptedDocumentVersionIds] = useState<
    Set<string>
  >(new Set());
  // What the customer typed inside each document and when it was shown, for
  // the soft hold request. Refs: they change on every keystroke and are only
  // read once, when the hold is created (odd/tasks/document-form-capture.md).
  const documentFormDataRef = useRef<Record<string, Record<string, string>>>(
    {},
  );
  const documentViewTimesRef = useRef<Record<string, DocumentViewTimes>>({});

  // Delivery state
  const [selectedDeliveryType, setDeliveryType] = useState<
    "pickup" | "delivery"
  >(initialDeliveryType ?? "pickup");
  const [selectedDeliveryOption, setDeliveryOption] = useState<
    "custom" | "location"
  >(initialDeliveryOption ?? "custom");
  const [chosenLocationId, setSelectedLocationId] = useState<string>(
    initialSelectedLocationId ?? "",
  );
  const [customAddress, setCustomAddress] = useState<CustomAddress | null>(
    initialCustomAddress ?? null,
  );
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(
    initialDeliveryDistance ?? null,
  );
  const [addressSelected, setAddressSelected] = useState(
    !!(
      initialCustomAddress &&
      initialDeliveryType === "delivery" &&
      initialDeliveryOption === "custom"
    ),
  );

  // Pricing state
  const [pricingBreakdown, setPricingBreakdown] =
    useState<PricingBreakdown | null>(null);
  const [isCalculatingPricing, setIsCalculatingPricing] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  // The server's `code` for the errors above, when it sent one (delivery
  // refusals), so the renter gets a one-click way out.
  const [pricingErrorCode, setPricingErrorCode] = useState<string | null>(null);

  // TaxCloud cart ID reference for persistence across pricing calls
  const taxcloudCartIdRef = useRef<string | null>(
    initialTaxcloudCartId ?? null,
  );

  // Payment state
  const [isProcessing, setIsProcessing] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutErrorCode, setCheckoutErrorCode] = useState<string | null>(
    null,
  );
  const [paymentError, setPaymentError] = useState<{
    code: string;
    declineCode: string;
    message: string;
  } | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);
  const [isCustomerFormValid, setIsCustomerFormValid] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [depositConsent, setDepositConsent] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [isFinalizing, setIsFinalizing] = useState(false);

  // Request to Book: set once the hold reaches `awaiting_approval | declined
  // | failed`. The checkout is over; only the outcome screen renders.
  const [requestSent, setRequestSent] = useState<{
    outcome: RequestOutcome;
    expiresAt: string | null;
    declineReason: string | null;
    failureReason: string | null;
    holdId: string;
    holdToken: string;
  } | null>(null);

  // Set after the rental is created (by the soft-hold polling) so the
  // post-pay deposit-hold flow can address it. `null` until the webhook has
  // run and the SoftHold status endpoint reports a rental.
  const [rentalReady, setRentalReady] = useState<{
    rentalId: string;
    accessToken: string | null;
  } | null>(null);

  // PaymentMethod id captured by CheckoutPaymentElementForm when the
  // customer confirms the rental. We hand this id to the deposit-hold flow
  // so the backend can try to reuse the card off-session before prompting
  // for a fresh one.
  const rentalPaymentMethodIdRef = useRef<string | null>(null);
  // Cancel of the hold left behind by a failed payment, so a retry can wait for it.
  const pendingHoldCancelRef = useRef<Promise<boolean> | null>(null);

  // Load add-ons
  const {
    data: addons = EMPTY_LIST,
    isLoading: isLoadingAddons,
    isSuccess: isAddonsLoaded,
  } = useVehicleAddons({
    apiKey,
    fleetId,
    vehicleId,
    apiBaseUrl,
  });

  // Load delivery locations
  const {
    data: deliveryLocations = EMPTY_LIST,
    isLoading: isLoadingLocations,
    isError: locationsFailed,
    refetch: refetchLocations,
  } = useDeliveryLocations({
    apiKey,
    vehicleId,
    apiBaseUrl,
  });

  // Load vehicle details for delivery configuration
  const apiClientConfig = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey: apiKey || "",
  };
  const {
    data: vehicleResponse,
    isFetched: isVehicleFetched,
    refetch: refetchVehicle,
  } = useVehicle(apiClientConfig, fleetId, vehicleId);
  const vehicle = vehicleResponse?.data;

  // The server refused a delivery the checkout was offering: what it knows
  // is stale, so reload it (once per kind of refusal, or a refusal that
  // persists would reload forever) and let the offer follow.
  const reloadedForRef = useRef(new Set<string>());
  const onDeliveryRefused = (code: string | undefined) => {
    if (!code || reloadedForRef.current.has(code)) return;
    if (code === "delivery_location_not_found") {
      reloadedForRef.current.add(code);
      void refetchLocations?.();
    } else if (
      code === "delivery_not_available" ||
      code === "custom_address_not_available"
    ) {
      reloadedForRef.current.add(code);
      void refetchVehicle?.();
    }
  };

  // Delivery is offered only for a vehicle that says so; until it loads (or
  // when the flag is false/absent) the checkout is pickup-only. Deriving the
  // effective type keeps every downstream use (pricing, soft hold, summary)
  // from ever seeing "delivery" for such a vehicle.
  // A failed vehicle fetch counts as unknown: no offer, pickup keeps working.
  // What is offered can also depend on the vehicle's assigned locations: a
  // vehicle that delivers but takes no typed address has only them (and
  // nowhere to deliver to without them), and a preselected location can only
  // be judged against them. Wait for the locations only in those cases; every
  // other vehicle is priced as soon as it is known.
  const deliveryMode = resolveDeliveryMode(vehicle, deliveryLocations);
  const deliveryOffered = deliveryMode !== "none";
  const customAddressOffered = deliveryMode === "both";
  const locationsDecideMode =
    isDeliveryOffered(vehicle) &&
    (!isCustomAddressOffered(vehicle) ||
      (selectedDeliveryType === "delivery" &&
        selectedDeliveryOption === "location" &&
        !!chosenLocationId));
  const locationsSettled = !isLoadingLocations;
  const deliveryModeKnown =
    isVehicleFetched && (locationsSettled || !locationsDecideMode);
  const deliveryType = deliveryOffered ? selectedDeliveryType : "pickup";
  // Locations-only vehicles never use a typed address; a vehicle with both
  // falls back to the typed address when there is no location to choose.
  const noLocationsToChoose =
    locationsSettled && deliveryLocations.length === 0;
  const deliveryOption = !customAddressOffered
    ? "location"
    : selectedDeliveryOption === "location" && noLocationsToChoose
      ? "custom"
      : selectedDeliveryOption;

  // A preselected location the vehicle does not have (stale URL or storage)
  // counts as no choice, so the renter picks one instead of the server
  // refusing it.
  // (Not when the request failed: the list is unknown, not empty.)
  const selectedLocationId =
    locationsSettled &&
    !locationsFailed &&
    !deliveryLocations.some((location) => location.id === chosenLocationId)
      ? ""
      : chosenLocationId;

  // Drop a preselected delivery (URL param, stored selection) once the
  // vehicle is known not to offer it, so it does not resurface later.
  useEffect(() => {
    // A failed locations request says nothing about a vehicle whose mode the
    // locations decide; for any other it changes nothing, so the reset holds.
    if (
      deliveryModeKnown &&
      !deliveryOffered &&
      !(locationsFailed && locationsDecideMode)
    ) {
      setDeliveryType("pickup");
    }
  }, [
    deliveryModeKnown,
    deliveryOffered,
    locationsFailed,
    locationsDecideMode,
  ]);

  // A typed address preselected for a vehicle that takes only its locations
  // is dropped: the choice falls back to the locations list.
  useEffect(() => {
    if (deliveryModeKnown && deliveryOffered && !customAddressOffered) {
      setDeliveryOption("location");
      setCustomAddress(null);
      setAddressSelected(false);
    }
  }, [deliveryModeKnown, deliveryOffered, customAddressOffered]);

  // Request to Book: the vehicle's EFFECTIVE mode decides the whole checkout
  // flavour. Instant vehicles (and an unknown mode) keep today's flow.
  const requestToBook = isRequestToBook(vehicle);
  const checkoutCopy = getCheckoutCopy(requestToBook);

  // Custom booking questions: one active catalog per flow
  // (`instant` | `request`). `flow` stays `undefined` until the vehicle is
  // fetched, so the hook never fetches the wrong flow's catalog and
  // re-fetches once the real one resolves — it fetches the right one from
  // the start. The questions section renders for EITHER flow now, whenever
  // that flow's active catalog is non-empty; it is no longer gated on
  // `requestToBook`.
  const flow = isVehicleFetched
    ? requestToBook
      ? "request"
      : "instant"
    : undefined;
  const { data: screeningFields = EMPTY_LIST } = useScreeningQuestionsCheckout({
    apiKey,
    fleetId,
    flow,
    apiBaseUrl,
  });
  const [screeningValues, setScreeningValues] = useState<ScreeningValues>({});
  const [screeningErrors, setScreeningErrors] = useState<ScreeningErrors>({});
  const handleScreeningChange = (questionId: number, value: string) => {
    const key = String(questionId);
    setScreeningValues((prev) => ({ ...prev, [key]: value }));
    setScreeningErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // Surface integrations that reach checkout without a resolvable vehicle name.
  useEffect(() => {
    const diagnostic = vehicleNameDiagnostic(
      { loaded: isVehicleFetched, vehicle },
      vehicleName,
      vehicleId,
    );

    if (diagnostic) console.error(diagnostic);
  }, [isVehicleFetched, vehicle, vehicleName, vehicleId]);

  // Security deposit gating
  const depositAmount =
    vehicle?.security_deposit !== undefined && vehicle.security_deposit !== null
      ? Number(vehicle.security_deposit)
      : 0;

  const depositConsentRequired =
    !!securityDepositMode &&
    securityDepositMode !== "disabled" &&
    depositAmount > 0;

  const creditCardRequired = pricingBreakdown?.credit_card_required === true;

  const depositConsentSatisfied = !depositConsentRequired || depositConsent;

  // The post-pay deposit-hold sub-flow only runs in `after_checkout` mode —
  // the other modes (at_checkin, manual) collect the deposit out of band.
  const requiresPostPayDepositHold =
    securityDepositMode === "after_checkout" && depositAmount > 0;

  const {
    stage: depositStage,
    errorMessage: depositErrorMessage,
    retryCount: depositRetryCount,
    scheduledAt: depositScheduledAt,
    setDepositPaymentMethodId,
    requestDepositHold,
    retry: retryDeposit,
  } = useDepositHoldFlow({
    enabled: !!rentalReady && requiresPostPayDepositHold,
    rentalId: rentalReady?.rentalId ?? null,
    accessToken: rentalReady?.accessToken ?? null,
    rentalPaymentMethodId: rentalPaymentMethodIdRef.current,
  });

  const {
    data: insuranceOptions,
    isLoading: isInsuranceLoading,
    isSuccess: isInsuranceLoaded,
  } = useInsuranceOptions({ apiKey, fleetId, vehicleId, apiBaseUrl });

  // FLEE-442: the drivers section. Shown when the fleet asks at checkout or a
  // selected option checks every driver; the cards are held in state only.
  const renterDriversConfig = useRenterDriversConfig(apiKey, apiBaseUrl);
  const selectedInsuranceOptions = (insuranceOptions ?? []).filter((option) =>
    selectedInsuranceOptionIds.includes(option.id),
  );
  const insurerChecksDrivers = selectedInsuranceOptions.some(
    (option) => option.requires_all_drivers,
  );
  const renterDrivers = useRenterDriverCards(
    to,
    renterDriversConfig,
    insurerChecksDrivers,
  );
  const driversRequired = computeDriversRequired(
    renterDriversConfig,
    selectedInsuranceOptions,
  );
  const driversBlockContinue = driversRequired && !renterDrivers.allReady;

  // Drop a selected option the unit no longer offers
  useEffect(() => {
    if (!isInsuranceLoaded || !insuranceOptions) return;
    const offered = new Set(insuranceOptions.map((option) => option.id));
    setSelectedInsuranceOptionIds((prev) =>
      prev.every((id) => offered.has(id))
        ? prev
        : prev.filter((id) => offered.has(id)),
    );
  }, [insuranceOptions, isInsuranceLoaded]);

  const { data: insuranceGroups = EMPTY_LIST } = useInsuranceOptionGroups({
    apiKey,
    fleetId,
    apiBaseUrl,
  });

  const {
    data: acceptanceDocuments = EMPTY_LIST,
    isLoading: isAcceptanceDocumentsLoading,
  } = useAcceptanceDocuments({ apiKey, vehicleId, apiBaseUrl });

  // Distance calculation state
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);

  // True when the customer selected an insurance option with terms but has not accepted them yet.
  const hasUnacceptedInsuranceTerms = (() => {
    const selectedSet = new Set(selectedInsuranceOptionIds);
    return (insuranceOptions ?? []).some(
      (opt) =>
        selectedSet.has(opt.id) &&
        !!opt.terms &&
        opt.terms.trim().length > 0 &&
        !acceptedInsuranceTerms[opt.id],
    );
  })();

  // True when the vehicle has a mandatory document the customer has not
  // explicitly checked yet (fleet-agreement-acceptance, path 1). This gate is
  // always enforced client-side, independent of the server's
  // `fleet_agreement_acceptance_enforced` flag. Also true while the
  // documents query is still loading — `acceptanceDocuments` defaults to
  // `[]` in flight, which would otherwise open the gate for the brief
  // window before the vehicle's real documents have loaded.
  const hasUnacceptedMandatoryDocuments =
    computeHasUnacceptedMandatoryDocuments(
      isAcceptanceDocumentsLoading,
      acceptanceDocuments,
      acceptedDocumentVersionIds,
    );

  // Cancel pending soft hold when parameters change
  const cancelPendingSoftHoldAsync = async (
    holdId: string,
    holdToken: string,
  ): Promise<boolean> => {
    try {
      const apiClient = {
        baseUrl: getApiBaseUrl(apiBaseUrl),
        apiKey: apiKey || "",
      };

      await softHolds.cancel(apiClient, holdId, holdToken);
      console.log("Previous soft hold cancelled successfully");

      // Clear client secret to reset payment form
      setClientSecret(null);
      return true;
    } catch (error) {
      console.warn("Error cancelling previous soft hold:", error);
      return false;
    }
  };

  // Auto-cancel previous hold when vehicle or dates change
  useEffect(() => {
    const existingHold = getPendingSoftHold();

    if (existingHold) {
      const hasChanged =
        existingHold.vehicle_id !== vehicleId ||
        existingHold.start_date !== from ||
        existingHold.end_date !== to;

      if (hasChanged) {
        console.log("Booking parameters changed, cancelling previous hold");
        cancelPendingSoftHoldAsync(
          existingHold.hold_id,
          existingHold.hold_token,
        );
        clearPendingSoftHold();
      }
    }
  }, [vehicleId, from, to]);

  // A pre-selected id can name an add-on this guest is not offered (a hidden
  // one smuggled in through a URL param). Prune once the list has loaded.
  useEffect(() => {
    if (!isAddonsLoaded) return;
    setSelectedAddonIds((prev) => keepOfferedAddonIds(prev, addons));
  }, [addons, isAddonsLoaded]);

  // Auto-select required addons
  useEffect(() => {
    const requiredIds = addons.filter((a) => a.required).map((a) => a.id);
    if (requiredIds.length > 0) {
      setSelectedAddonIds((prev) => [...new Set([...prev, ...requiredIds])]);
    }
  }, [addons]);

  // Load Stripe with connected account
  useEffect(() => {
    // If there's a config error (like no merchant account), don't try to load Stripe
    if (configError) {
      console.log(
        "Config error present, skipping Stripe initialization:",
        configError,
      );
      return;
    }

    // Only load Stripe if we have both the publishable key and external account ID
    if (stripePublishableKey && externalAccountId) {
      console.log("Loading Stripe with account:", externalAccountId);
      setStripePromise(
        loadStripe(stripePublishableKey, {
          stripeAccount: externalAccountId,
        }),
      );
    } else if (!configLoading) {
      // If config is done loading but we don't have an account ID, log a warning
      console.warn("Config loaded but missing data:", {
        hasPublishableKey: !!stripePublishableKey,
        hasAccountId: !!externalAccountId,
      });
    }
  }, [stripePublishableKey, externalAccountId, configError, configLoading]);

  // Reset delivery distance when switching away from custom delivery. Wait
  // for the vehicle: until it is known the effective type is "pickup", and
  // that must not wipe a distance that arrived with the preselected delivery.
  useEffect(() => {
    if (!deliveryModeKnown) return;
    if (deliveryType !== "delivery" || deliveryOption !== "custom") {
      setDeliveryDistance(null);
    }
  }, [deliveryModeKnown, deliveryType, deliveryOption]);

  // Calculate real distance when custom delivery address is selected
  useEffect(() => {
    const calculateRealDistance = async () => {
      if (
        deliveryType !== "delivery" ||
        deliveryOption !== "custom" ||
        !customAddress ||
        !addressSelected ||
        !vehicle ||
        !googleMapsApiKey
      ) {
        return;
      }

      // Validate that coordinates are valid (not 0,0)
      if (
        !customAddress.lat ||
        !customAddress.lng ||
        (customAddress.lat === 0 && customAddress.lng === 0)
      ) {
        return;
      }

      setIsCalculatingDistance(true);

      try {
        // Build vehicle pickup address
        const vehicleAddressParts = [
          vehicle.pickup_address,
          vehicle.pickup_city,
          vehicle.pickup_state,
          vehicle.pickup_zip_code,
        ].filter(Boolean);

        if (vehicleAddressParts.length === 0) {
          setIsCalculatingDistance(false);
          return;
        }

        const vehicleAddress = vehicleAddressParts.join(", ");

        // Calculate real distance using Google Maps Distance Matrix API
        const result = await calculateDeliveryFee(
          vehicleAddress,
          {
            lat: customAddress.lat,
            lng: customAddress.lng,
          },
          0, // We don't calculate fees here, just distance
          0,
        );

        if (result.distance !== null) {
          // Format distance to 2 decimals
          const distance = parseFloat(result.distance.toFixed(2));
          setDeliveryDistance(distance);
        } else {
          setDeliveryDistance(null);
        }
      } catch (error) {
        console.error("Failed to calculate delivery distance:", error);
        setDeliveryDistance(null);
      } finally {
        setIsCalculatingDistance(false);
      }
    };

    calculateRealDistance();
  }, [
    deliveryType,
    deliveryOption,
    customAddress,
    addressSelected,
    vehicle,
    googleMapsApiKey,
  ]);

  const getDeliveryOptions = useCallback(() => {
    if (deliveryType === "delivery") {
      if (deliveryOption === "custom" && customAddress) {
        return {
          delivery_type: "delivery",
          address_type: "custom",
          delivery_distance: deliveryDistance,
          custom_address: {
            address: customAddress.address,
            city: customAddress.city,
            state: customAddress.state,
            zip_code: customAddress.zipCode || "",
            country: customAddress.country,
          },
        };
      } else if (deliveryOption === "location" && selectedLocationId) {
        return {
          delivery_type: "delivery",
          address_type: "predefined",
          delivery_location_id: selectedLocationId,
        };
      }
    }
    return {
      delivery_type: "pickup",
    };
  }, [
    deliveryType,
    deliveryOption,
    customAddress,
    deliveryDistance,
    selectedLocationId,
  ]);

  // Counts the pricing requests: only the latest one may change the state,
  // so an older response (quote or refusal) arriving late is ignored.
  const pricingRequestRef = useRef(0);

  const calculateFullPricing = useCallback(async () => {
    const requestId = ++pricingRequestRef.current;
    const isCurrent = () => requestId === pricingRequestRef.current;
    // A superseded request no longer clears this flag itself.
    setIsCalculatingPricing(false);
    if (!vehicleId || !from || !to) return;

    // Wait for distance calculation to complete if in progress
    if (isCalculatingDistance) {
      return;
    }

    // Every new attempt starts without a delivery refusal.
    setPricingErrorCode(null);

    // Validate delivery if selected
    if (deliveryType === "delivery") {
      if (deliveryOption === "custom" && (!customAddress || !addressSelected)) {
        setPricingBreakdown(null);
        setPricingError(
          "Please select a delivery address to calculate pricing",
        );
        return;
      }

      // If custom delivery is selected, ensure distance is calculated
      if (deliveryOption === "custom" && deliveryDistance === null) {
        setPricingBreakdown(null);
        setPricingError("Calculating delivery distance...");
        return;
      }

      if (deliveryOption === "location" && !selectedLocationId) {
        setPricingBreakdown(null);
        setPricingError("Please select a delivery location");
        return;
      }
    }

    // Validate distance against max delivery radius
    if (
      deliveryType === "delivery" &&
      deliveryOption === "custom" &&
      vehicle?.max_delivery_radius_miles &&
      deliveryDistance !== null &&
      deliveryDistance > Number(vehicle.max_delivery_radius_miles)
    ) {
      setPricingBreakdown(null);
      setPricingError(
        `Delivery distance (${deliveryDistance.toFixed(2)} miles) exceeds the maximum allowed radius of ${Number(vehicle.max_delivery_radius_miles)} miles for this vehicle. Please select a closer delivery address.`,
      );
      return;
    }

    setIsCalculatingPricing(true);
    setPricingError(null);
    setPricingErrorCode(null);

    try {
      const apiClient = {
        baseUrl: getApiBaseUrl(apiBaseUrl),
        apiKey: apiKey || "",
      };

      // Build pricing request
      const pricingRequest: any = {
        quotable_type: "fleet_vehicle",
        quotable_id: vehicleId,
        pick_up: from,
        return: to,
        selected_addon_ids: selectedAddonIds,
        selected_insurance_option_ids: selectedInsuranceOptionIds,
        delivery_options: getDeliveryOptions(),
        taxcloud_cart_id: taxcloudCartIdRef.current || undefined,
      };

      const response = await quotes.calculatePricing(
        apiClient,
        fleetId,
        pricingRequest,
      );

      // The cart id is kept even from a superseded quote, so the next one
      // does not open another cart.
      if (response.data?.taxcloud_cart_id) {
        taxcloudCartIdRef.current = response.data.taxcloud_cart_id;
      }
      if (!isCurrent()) return;
      if (response.data) {
        setPricingBreakdown(response.data);
        setPricingError(null);
      }
    } catch (error: any) {
      if (!isCurrent()) return;
      console.error("Error calculating pricing:", error);

      // Extract error message from response
      let errorMessage = "Failed to calculate pricing. Please try again.";

      // Try to parse the error message if it's a JSON string
      if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          if (parsedError?.error?.message) {
            errorMessage = parsedError.error.message;
          } else if (parsedError?.message) {
            errorMessage = parsedError.message;
          } else {
            errorMessage = error.message;
          }
        } catch {
          // If parsing fails, use the error message as is
          errorMessage = error.message;
        }
      } else if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (typeof error === "string") {
        try {
          const parsedError = JSON.parse(error);
          if (parsedError?.error?.message) {
            errorMessage = parsedError.error.message;
          } else if (parsedError?.message) {
            errorMessage = parsedError.message;
          } else {
            errorMessage = error;
          }
        } catch {
          errorMessage = error;
        }
      }

      const errorCode = extractErrorCode(error);
      const deliveryInfo = getDeliveryErrorInfo(errorCode);
      onDeliveryRefused(errorCode);
      setPricingErrorCode(deliveryInfo ? (errorCode ?? null) : null);
      setPricingError(
        deliveryInfo
          ? deliveryInfo.message
          : typeof errorMessage === "string"
            ? errorMessage
            : "Failed to calculate pricing. Please try again.",
      );
      setPricingBreakdown(null);
    } finally {
      if (isCurrent()) setIsCalculatingPricing(false);
    }
  }, [
    vehicleId,
    from,
    to,
    selectedAddonIds,
    deliveryType,
    deliveryOption,
    selectedLocationId,
    customAddress,
    deliveryDistance,
    addressSelected,
    vehicle,
    apiKey,
    fleetId,
    apiBaseUrl,
    selectedInsuranceOptionIds,
  ]);

  // Recalculate pricing when dependencies change. Hold the first quote until
  // the vehicle is known: whether delivery is offered decides what to price,
  // and a throwaway pickup quote would race the real one.
  useEffect(() => {
    // Only trigger if not currently calculating distance
    if (deliveryModeKnown && !isCalculatingDistance) {
      calculateFullPricing();
    }
  }, [
    deliveryModeKnown,
    selectedAddonIds,
    selectedInsuranceOptionIds,
    deliveryType,
    deliveryOption,
    selectedLocationId,
    customAddress,
    addressSelected,
    deliveryDistance,
    isCalculatingDistance,
  ]);

  // Update soft hold when pricing changes (addons/insurance/delivery)
  useEffect(() => {
    const existingHold = getPendingSoftHold();

    // Only update if we have an active hold and new pricing. Wait for the
    // vehicle too: until it is known the quote is priced as pickup, and
    // syncing that to a hold the renter built with delivery would rewrite it.
    if (
      !existingHold ||
      !pricingBreakdown ||
      isCalculatingPricing ||
      !deliveryModeKnown
    ) {
      return;
    }

    // Helper to compare arrays (order-independent)
    function arraysEqual(a: string[], b: string[]) {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    }

    // Helper to compare delivery options
    function deliveryStartEqual(
      hold: PendingSoftHold,
      dType: string,
      dOption: string,
      locId: string,
      addr: CustomAddress | null,
    ) {
      // Since SoftHold doesn't store granule delivery state in the top level localStorage object
      // check if we can infer it or if we should just rely on the fact that pricing changed.
      // Actually, relying on pricingBreakdown alone might be risky if we don't know what the hold "has".
      // But we can check if the current `getDeliveryOptions()` result is different
      // from what we might have expected?
      // The easiest way is to let "pricingBreakdown" trigger it, but we need to know if the hold
      // is stale.

      // Better approach: Since we don't store delivery details in PENDING_SOFT_HOLD_KEY explicitly
      // other than maybe implicit total, we should update whenever these change if we have an active hold.
      // However, to avoid infinite loops, we need to be careful.
      // Logic: If user changes delivery -> `calculateFullPricing` runs -> `pricingBreakdown` updates.
      // THEN this effect runs.
      // If we don't check for changes against "what's on the hold", we might update infinitely.
      // But `existingHold` is from localStorage.
      // We should probably trust that if `pricingBreakdown` was just updated (and it's different/newer),
      // we might want to sync soft hold.
      // Let's add explicit tracking or just check if the inputs have stabilized.

      // Simplest robust fix: Trigger update on any delivery change that resulted in a pricing change.
      // We can check if the hold's total_amount matches the new pricingBreakdown.total?
      // No, total matching isn't enough (could be coincidence).

      return false; // Force update check
    }

    // Check if addons or insurance changed
    const addonsChanged = !arraysEqual(
      existingHold.selected_addon_ids || [],
      selectedAddonIds,
    );
    const insuranceChanged = !arraysEqual(
      existingHold.selected_insurance_option_ids || [],
      selectedInsuranceOptionIds,
    );

    // Check if delivery changed
    // We assume that if `pricingBreakdown` changed, we might need to update.
    // But we need to avoid loops.
    // Let's look at the `updateSoftHoldMetadata` logic: it updates localStorage at the end.
    // If we add delivery info to localStorage, we could diff it.
    // For now, let's assuming if the other dependencies changed, we update.
    // But wait, delivery changes ARE dependencies now.

    // We need a way to know if the current delivery configuration matches what's on the hold.
    // Since we don't store delivery config in `PendingSoftHold`, we are flying blind slightly.
    // However, `updateSoftHoldMetadata` is sending `getDeliveryOptions()`.
    // If we rely on valid user interaction:
    // User clicks "Custom Delivery" -> State Change -> Pricing Calc -> Pricing Updated -> This Effect.
    // We should trigger update.
    // Is there a risk of loop?
    // `updateSoftHoldMetadata` updates `localStorage`. `getPendingSoftHold` reads it.
    // If we don't save delivery options to localStorage, `existingHold` won't change regarding delivery.
    // So `deliveryChanged` would always be true if we compare local state vs "nothing".
    // AND `updateSoftHoldMetadata` doesn't seem to verify if it *needs* to update other than blindly sending.

    // Solution: We should assume that if `deliveryType` etc changed, we MUST update the hold.
    // But we need to debounce or ensure we only do it once per change.
    // Actually, `calculateFullPricing` is debounced/effect-driven.
    // This effect runs after `pricingBreakdown` updates.
    // So it's safe to update the hold ONCE after pricing update.
    // The issue is distinguishing "I just loaded" from "User changed something".

    // Let's use a ref to track the last synced delivery state?
    // Or just be aggressive: if we have a hold, and we have pricing, sync them.
    // The backend `update` is relatively cheap.
    // To prevent loops, we can check if the hold's `total_amount` is different from `pricingBreakdown.total`.
    // If they differ, sync! This is a great proxy for "something changed affecting price".
    // Also check addons/insurance for non-price changes (e.g. swapping free addons).

    const priceChanged = existingHold.total_amount !== pricingBreakdown.total;

    if (addonsChanged || insuranceChanged || priceChanged) {
      console.log("Booking/Pricing changed, updating soft hold...", {
        addonsChanged,
        insuranceChanged,
        priceChanged,
        oldTotal: existingHold.total_amount,
        newTotal: pricingBreakdown.total,
      });

      updateSoftHoldMetadata(existingHold.hold_id, existingHold.hold_token);
    }
  }, [
    pricingBreakdown,
    selectedAddonIds,
    selectedInsuranceOptionIds,
    isCalculatingPricing,
    deliveryModeKnown,
    // Add delivery dependencies to ensure we re-evaluate when they settle
    // (Though usually they trigger pricing update which triggers this)
    deliveryType,
    deliveryOption,
    selectedLocationId,
    // customAddress // customAddress might be an object, referencing might be unstable.
    // Rely on pricingBreakdown update instead which captures the result of address change.
  ]);

  // Update soft hold metadata when addons/insurance change
  const updateSoftHoldMetadata = async (holdId: string, holdToken: string) => {
    try {
      const apiClient = {
        baseUrl: getApiBaseUrl(apiBaseUrl),
        apiKey: apiKey || "",
      };

      const payload = {
        total_amount: "0.00", // Ignored by backend, but required by type
        metadata: {
          selected_addon_ids: selectedAddonIds,
          selected_insurance_option_ids: selectedInsuranceOptionIds,
          delivery_options: getDeliveryOptions(),
          adults,
          children,
          infants,
          pets,
          payment_method_type: "card",
        },
      };

      await softHolds.update(apiClient, holdId, holdToken, payload);
      console.log("Soft hold metadata updated successfully");

      // Update local storage
      const existing = getPendingSoftHold();
      if (existing) {
        setPendingSoftHold({
          ...existing,
          selected_addon_ids: selectedAddonIds,
          selected_insurance_option_ids: selectedInsuranceOptionIds,
          // total_amount is now managed by backend/stripe
        });
      }
    } catch (error) {
      console.error("Failed to update soft hold metadata:", error);
      // The re-priced set now needs drivers the hold does not have: drop the
      // hold so the renter adds them and Continue creates a fresh one.
      const driverError = parseRenterDriverError(error);
      if (driverError?.code === "renter_drivers_required") {
        invalidateLocalSoftHold();
        renterDrivers.markTried();
        setCheckoutError(
          driverError.message || "Add every driver's license to continue.",
        );
        return;
      }
      // A delivery the vehicle no longer offers: say so instead of leaving
      // the renter with a hold that does not match what they chose.
      const code = extractErrorCode(error);
      const info = getDeliveryErrorInfo(code);
      if (info && code) {
        setCheckoutError(info.message);
        setCheckoutErrorCode(code);
        onDeliveryRefused(code);
      }
    }
  };

  const getPaymentErrorMessage = (
    declineCode: string,
    defaultMessage: string,
  ) => {
    const errorMessages: Record<string, string> = {
      generic_decline: "Your card was declined. Please try a different card.",
      insufficient_funds: "Insufficient funds. Please try a different card.",
      lost_card:
        "This card has been reported as lost. Please contact your bank.",
      stolen_card:
        "This card has been reported as stolen. Please contact your bank.",
      expired_card: "Your card has expired. Please use a different card.",
      incorrect_cvc:
        "The security code is incorrect. Please check and try again.",
      processing_error: "A processing error occurred. Please try again.",
      do_not_honor:
        "Your bank declined this transaction. Please try a different card.",
      restricted_card: "This card cannot be used for this transaction.",
      transaction_not_allowed: "This transaction is not allowed on this card.",
      try_again_later: "Please try again later or use a different card.",
      withdrawal_count_limit_exceeded:
        "Daily withdrawal limit exceeded. Please try tomorrow.",
    };

    return errorMessages[declineCode] || defaultMessage;
  };

  // One-click ways out of a delivery refusal. The vehicle data the checkout
  // loaded may be stale, so the choice is changed locally and re-priced.
  const recoverToPickup = () => {
    setDeliveryType("pickup");
    setCheckoutError(null);
    setCheckoutErrorCode(null);
  };
  const recoverToLocations = () => {
    setDeliveryType("delivery");
    setDeliveryOption("location");
    setSelectedLocationId("");
    setCustomAddress(null);
    setAddressSelected(false);
    setCheckoutError(null);
    setCheckoutErrorCode(null);
  };

  // A delivery refusal is moot once the checkout is on pickup (the reload
  // showed the vehicle no longer delivers): do not leave it on screen.
  useEffect(() => {
    if (deliveryType === "pickup" && getDeliveryErrorInfo(checkoutErrorCode)) {
      setCheckoutError(null);
      setCheckoutErrorCode(null);
    }
  }, [deliveryType, checkoutErrorCode]);

  const clearErrors = () => {
    setCheckoutError(null);
    setCheckoutErrorCode(null);
    setPaymentError(null);
    setIsProcessing(false);
  };

  /**
   * Invalidate the local soft hold + client secret so the next "Continue to
   * Payment" click creates a fresh PaymentIntent. We call this when Stripe
   * rejects the confirmation — the cached PI may be unusable (e.g. it was
   * created before a server-side PI-options change) and reusing it would
   * surface the same error forever.
   *
   * The backend hold is cancelled too: left alone it keeps the vehicle
   * blocked for 30 minutes, so the retry's new hold would be refused with
   * "Vehicle not available for selected dates". The cancel is best effort
   * (a failure only means the old hold lapses on its own) and the next
   * `initializeCheckout` waits for it before creating the replacement.
   */
  const invalidateLocalSoftHold = () => {
    const staleHold = getPendingSoftHold();
    clearPendingSoftHold();
    setClientSecret(null);
    rentalPaymentMethodIdRef.current = null;

    if (staleHold) {
      pendingHoldCancelRef.current = cancelPendingSoftHoldAsync(
        staleHold.hold_id,
        staleHold.hold_token,
      );
    }
  };

  const handlePaymentFailure = (msg: string) => {
    setCheckoutError(msg);
    invalidateLocalSoftHold();
  };

  const finishCheckout = useCallback(
    (rentalId: string) => {
      clearPendingSoftHold();
      if (onSuccess) onSuccess(rentalId);
      if (onNavigateToSuccess) onNavigateToSuccess(rentalId);
    },
    [onSuccess, onNavigateToSuccess],
  );

  const pollForRentalCreation = useCallback(
    async (holdId: string, holdToken: string, attempts = 0) => {
      if (attempts > 30) {
        setIsFinalizing(false);
        setCheckoutError(
          "Timeout waiting for rental confirmation. Please check your email for confirmation.",
        );
        return;
      }

      try {
        const apiClient = {
          baseUrl: getApiBaseUrl(apiBaseUrl),
          apiKey: apiKey || "",
        };

        const { data } = await softHolds.get(apiClient, holdId, holdToken);

        const pollResult = resolveHoldPoll(data);

        if (
          pollResult.kind === "request" &&
          pollResult.outcome === "failed" &&
          isCreditCardRequiredFailure(pollResult.failureCode) &&
          !requestToBook
        ) {
          // A debit or prepaid card can't back the deposit. Send the renter
          // back to the payment step to pay with another card.
          setIsFinalizing(false);
          setCheckoutError(creditCardRequiredMessage(pollResult.failureReason));
          clearPendingSoftHold();
          setClientSecret(null);
          rentalPaymentMethodIdRef.current = null;
        } else if (pollResult.kind === "request") {
          // Stop polling. Forget the local hold so changing the dates later
          // cannot auto-cancel a request the fleet is already reviewing.
          setIsFinalizing(false);
          clearPendingSoftHold();
          setRequestSent({
            outcome: pollResult.outcome,
            expiresAt: pollResult.expiresAt,
            declineReason: pollResult.declineReason,
            failureReason: pollResult.failureReason,
            holdId,
            holdToken,
          });
        } else if (pollResult.kind === "completed") {
          setIsFinalizing(false);

          // Only hand control to the post-pay deposit-hold sub-flow when it
          // actually renders (after_checkout mode). For other modes (manual,
          // at_checkin) the deposit is collected out of band, so navigate
          // straight to the success screen.
          if (requiresPostPayDepositHold) {
            setRentalReady({
              rentalId: pollResult.rentalId,
              accessToken: pollResult.accessToken,
            });
          } else {
            finishCheckout(pollResult.rentalId);
          }
        } else {
          setTimeout(
            () => pollForRentalCreation(holdId, holdToken, attempts + 1),
            2000,
          );
        }
      } catch (error) {
        console.warn("Polling error:", error);
        setTimeout(
          () => pollForRentalCreation(holdId, holdToken, attempts + 1),
          2000,
        );
      }
    },
    [
      apiBaseUrl,
      apiKey,
      requiresPostPayDepositHold,
      requestToBook,
      finishCheckout,
    ],
  );

  const handlePaymentSuccess = () => {
    if (bookingId) {
      if (requiresPostPayDepositHold) {
        setRentalReady({ rentalId: bookingId, accessToken: null });
      } else {
        finishCheckout(bookingId);
      }
      return;
    }

    const hold = getPendingSoftHold();
    if (hold) {
      setIsFinalizing(true);
      pollForRentalCreation(hold.hold_id, hold.hold_token);
    } else {
      setCheckoutError("Session expired. Please refresh.");
    }
  };

  const handleCustomerFormValidation = (isValid: boolean) => {
    setIsCustomerFormValid(isValid);
  };

  const handleSmsOptInChange = (optedIn: boolean) => {
    setSmsOptIn(optedIn);
  };

  // True at the very first render when Stripe has redirected back here after
  // confirming a PaymentIntent. We use this to suppress the payment form
  // (preventing a second `confirmPayment` on a PI that already succeeded) and
  // to bounce immediately to the dedicated finalize page that owns polling.
  const isReturningFromStripe = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    const status = params.get("redirect_status");
    return status === "succeeded" || status === "pending";
  }, []);

  // Bounce the customer to /embed/checkout/finalize whenever Stripe drops
  // them back on /embed/checkout after a redirect-based confirm (3DS / SCA).
  // The finalize page only polls; it never re-mounts a PaymentElement, which
  // is what was causing `payment_intent_unexpected_state` errors.
  useEffect(() => {
    if (!isReturningFromStripe) return;
    if (typeof window === "undefined") return;

    const hold = getPendingSoftHold();
    if (!hold) {
      // No local hold reference — fall back to in-page polling so we don't
      // strand the customer if they cleared storage between tabs.
      handlePaymentSuccess();
      return;
    }

    const finalizeParams = new URLSearchParams({
      hold_id: hold.hold_id,
      hold_token: hold.hold_token,
      api_key: apiKey || "",
      fleet_id: fleetId || "",
    });

    window.location.replace(
      `/embed/checkout/finalize?${finalizeParams.toString()}`,
    );
  }, [isReturningFromStripe, apiKey, fleetId, handlePaymentSuccess]);

  const initializeCheckout = async () => {
    setIsProcessing(true);
    setCheckoutError(null);
    setCheckoutErrorCode(null);
    setPaymentError(null);

    try {
      const apiClient = {
        baseUrl: getApiBaseUrl(apiBaseUrl),
        apiKey: apiKey || "",
      };

      if (driversBlockContinue) {
        renterDrivers.markTried();
        setCheckoutError("Add and save every driver's license to continue.");
        return;
      }

      // Check the fleet's custom booking questions (whichever flow's
      // catalog is active) before any hold or PaymentIntent exists. The
      // server re-validates them regardless.
      let screeningPayload: ReturnType<typeof buildScreeningPayload> | null =
        null;
      if (screeningFields.length > 0) {
        const errors = validateScreeningAnswers(
          screeningFields,
          screeningValues,
        );
        setScreeningErrors(errors);
        if (Object.keys(errors).length > 0) {
          setCheckoutError("Please answer the highlighted questions.");
          return;
        }
        screeningPayload = buildScreeningPayload(
          screeningFields,
          screeningValues,
        );
      }

      // Build soft hold request
      const softHoldRequest = buildSoftHoldRequest({
        vehicleId,
        startDate: from,
        endDate: to,
        customerEmail:
          (document.getElementById("customer_email") as HTMLInputElement)
            ?.value || "",
        customerName:
          (document.getElementById("customer_name") as HTMLInputElement)
            ?.value || "",
        customerPhone:
          (document.getElementById("customer_phone") as HTMLInputElement)
            ?.value || "",
        depositAcknowledged: depositConsent,
        selectedAddonIds,
        selectedInsuranceOptionIds,
        deliveryOptions: getDeliveryOptions(),
        adults,
        children,
        infants,
        pets,
        acceptedDocumentVersionIds: Array.from(acceptedDocumentVersionIds),
        documentFormData: documentFormDataRef.current,
        documentEvidence: await buildEvidenceForAcceptedDocuments({
          documents: acceptanceDocuments,
          acceptedVersionIds: acceptedDocumentVersionIds,
          valuesByVersion: documentFormDataRef.current,
          timesByVersion: documentViewTimesRef.current,
        }),
        screeningAnswers: screeningPayload?.screeningAnswers,
      });

      // The drivers travel only in this request body, and only when required
      // (the server ignores them otherwise). Never stored or logged here.
      const holdRequest = driversRequired
        ? { ...softHoldRequest, ...renterDrivers.buildRequest() }
        : softHoldRequest;

      // IP EVIDENCE (fleet-agreement-acceptance): this call MUST stay a
      // browser-side fetch (via `clientWithApiClient`), never a Next.js
      // server action or route-handler proxy. The server records
      // `conn.remote_ip` as the customer's acceptance IP; proxying through a
      // Next server would record Vercel's IP instead — the exact bug in
      // `bugs/fms-api-auth-sessions-store-next-server-ip`. `CheckoutContainer`
      // is a widget component with no server-side rendering path, so this is
      // structurally guaranteed today; keep it that way.
      await pendingHoldCancelRef.current;
      pendingHoldCancelRef.current = null;

      const result = await softHolds.create(apiClient, fleetId, holdRequest);

      if (result.data) {
        // Save soft hold to localStorage for session persistence
        setPendingSoftHold({
          hold_id: result.data.hold_id,
          hold_token: result.data.hold_token,
          hold_expires_at: result.data.expires_at,
          vehicle_id: vehicleId,
          start_date: from,
          end_date: to,
          selected_addon_ids: selectedAddonIds,
          selected_insurance_option_ids: selectedInsuranceOptionIds,
          total_amount: (result.data as any).total_amount || "0.00", // Use backend calculated amount
        });

        // Set client secret for Stripe Payment Element
        if (result.data.client_secret) {
          setClientSecret(result.data.client_secret);
        } else {
          setCheckoutError("No client secret returned from server");
        }
      } else {
        const rawError = (result as any).error?.message;
        const resultCode = extractErrorCode(result);
        const deliveryInfo = getDeliveryErrorInfo(resultCode);
        const errorMessage =
          deliveryInfo?.message ??
          (typeof rawError === "string"
            ? rawError
            : "Failed to create soft hold");
        setCheckoutErrorCode(deliveryInfo ? (resultCode ?? null) : null);
        onDeliveryRefused(resultCode);
        setCheckoutError(errorMessage);
        if (onError) onError(errorMessage);
      }
    } catch (err: unknown) {
      const driverError = parseRenterDriverError(err);
      if (driverError) {
        renterDrivers.applyServerError(driverError);
        const driverMessage =
          driverError.message || "Check the highlighted driver details.";
        setCheckoutError(driverMessage);
        if (onError) onError(driverMessage);
        return;
      }

      console.error("Checkout initialization error:", err);
      // The server's own message (notice period, screening, availability) is
      // shown as-is; a screening 422 also flags the offending questions.
      const {
        message: errorMessage,
        fieldErrors,
        code,
      } = parseSoftHoldError(err, screeningFields);
      setScreeningErrors(fieldErrors);

      setCheckoutErrorCode(code ?? null);
      onDeliveryRefused(code);
      setCheckoutError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  if (requestSent) {
    return (
      <BookingRequestOutcome
        outcome={requestSent.outcome}
        expiresAt={requestSent.expiresAt}
        declineReason={requestSent.declineReason}
        failureReason={requestSent.failureReason}
        onCancel={async () => {
          await softHolds.cancel(
            { baseUrl: getApiBaseUrl(apiBaseUrl), apiKey: apiKey || "" },
            requestSent.holdId,
            requestSent.holdToken,
          );
        }}
      />
    );
  }

  // Show loading only if config is loading OR (config is ready but Stripe is not yet loaded and there's no error)
  if (configLoading || (!stripePromise && !configError)) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center bg-white rounded-xl border border-gray-200 p-12">
          <div className="animate-spin h-12 w-12 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-6"></div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {configLoading ? "Loading configuration..." : "Loading checkout"}
          </h3>
          <p className="text-gray-600">
            Setting up your secure payment experience...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col items-center sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          {/* Logo. Prefer the fleet business profile (same authoritative source
              as the other embed headers); the config payload sometimes omits
              the logo, which would fall back to the generic FleetSync logo. */}
          {(fleetProfile?.logo_url ||
            businessProfile?.logo_url ||
            defaultLogoUrl) &&
            (() => {
              const logo = (
                <img
                  src={
                    fleetProfile?.logo_url ||
                    businessProfile?.logo_url ||
                    defaultLogoUrl
                  }
                  alt={fleetProfile?.name || businessProfile?.name || "Logo"}
                  className="h-10 sm:h-12 w-auto object-contain"
                  onError={(e) => {
                    // Attempt the default logo at most once, then hide the image.
                    // Comparing currentTarget.src (absolute) against a relative
                    // defaultLogoUrl never matches and would loop forever.
                    const img = e.currentTarget;
                    if (defaultLogoUrl && img.dataset.fallbackTried !== "1") {
                      img.dataset.fallbackTried = "1";
                      img.src = defaultLogoUrl;
                    } else {
                      img.style.display = "none";
                    }
                  }}
                />
              );

              return (
                <div className="flex-shrink-0">
                  {logoHref ? (
                    <a
                      href={logoHref}
                      target={openInNewTab ? "_blank" : "_top"}
                      rel={openInNewTab ? "noopener noreferrer" : undefined}
                      aria-label={
                        fleetProfile?.name || businessProfile?.name || "Logo"
                      }
                      className="inline-flex items-center"
                    >
                      {logo}
                    </a>
                  ) : (
                    logo
                  )}
                </div>
              );
            })()}
          {/* Title and subtitle */}
          <div className="text-center sm:text-right">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              Complete Your Reservation
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Customize your rental and provide payment information
            </p>
          </div>
        </div>

        {/* Merchant Account Warning Banner */}
        {configError?.code === "no_merchant_account" && (
          <div className="mb-6">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-yellow-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-semibold text-yellow-800">
                    Payments Not Configured
                  </h3>
                  <p className="text-sm text-yellow-700 mt-2">
                    This fleet hasn't configured their merchant account yet.
                    Payment methods and checkout are currently unavailable.
                  </p>
                  <p className="text-sm text-yellow-700 mt-2 font-medium">
                    Please contact the fleet administrator to set up payment
                    processing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Rental Documents Section (fleet-agreement-acceptance, path 1) */}
            {(isAcceptanceDocumentsLoading ||
              acceptanceDocuments.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Rental Documents
                </h2>
                <AcceptanceDocumentsSection
                  documents={acceptanceDocuments}
                  acceptedVersionIds={acceptedDocumentVersionIds}
                  onChange={setAcceptedDocumentVersionIds}
                  onViewed={(versionId) => {
                    documentViewTimesRef.current[versionId] = {
                      ...documentViewTimesRef.current[versionId],
                      first:
                        documentViewTimesRef.current[versionId]?.first ??
                        new Date().toISOString(),
                    };
                  }}
                  onFormData={(versionId, values) => {
                    documentFormDataRef.current = {
                      ...documentFormDataRef.current,
                      [versionId]: values,
                    };
                    documentViewTimesRef.current[versionId] = {
                      ...documentViewTimesRef.current[versionId],
                      last: new Date().toISOString(),
                    };
                  }}
                  isLoading={isAcceptanceDocumentsLoading}
                />
              </div>
            )}

            {/* Insurance Options Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Insurance Options
              </h2>
              <InsuranceOptionSelector
                options={insuranceOptions || []}
                groups={insuranceGroups}
                selectedIds={selectedInsuranceOptionIds}
                onChange={setSelectedInsuranceOptionIds}
                isLoading={isInsuranceLoading}
                acceptedTermsByOptionId={acceptedInsuranceTerms}
                onTermsAcceptanceChange={setAcceptedInsuranceTerms}
              />
            </div>

            {/* Add-ons Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Add-ons & Extras
              </h2>
              <AddonSelector
                addons={addons}
                selectedAddonIds={selectedAddonIds}
                onSelectionChange={setSelectedAddonIds}
                isLoading={isLoadingAddons}
              />
            </div>

            {/* Delivery Options Section: held back until the vehicle says
                whether it offers delivery, so it never flashes an offer. */}
            {deliveryModeKnown && !!vehicle && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Delivery Options
                </h2>
                <DeliveryOptionsSelector
                  deliveryType={deliveryType}
                  onDeliveryTypeChange={setDeliveryType}
                  deliveryOption={deliveryOption}
                  onDeliveryOptionChange={setDeliveryOption}
                  selectedLocationId={selectedLocationId}
                  onLocationSelect={setSelectedLocationId}
                  customAddress={customAddress}
                  onCustomAddressChange={setCustomAddress}
                  deliveryLocations={deliveryLocations}
                  deliveryFee={
                    pricingBreakdown?.breakdown?.delivery?.total
                      ? parseFloat(pricingBreakdown.breakdown.delivery.total)
                      : 0
                  }
                  deliveryDistance={deliveryDistance}
                  isCalculatingDistance={isCalculatingDistance}
                  deliveryAvailable={deliveryOffered}
                  customAddressAvailable={customAddressOffered}
                  onAddressSelected={setAddressSelected}
                  googleMapsApiKey={googleMapsApiKey}
                />
              </div>
            )}

            {/* Customer & Payment Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Customer Information
              </h2>

              <CustomerInfoForm
                onClearErrors={clearErrors}
                onValidationChange={handleCustomerFormValidation}
              />

              {screeningFields.length > 0 && (
                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">
                    About your trip
                  </h3>
                  <ScreeningQuestionsSection
                    fields={screeningFields}
                    values={screeningValues}
                    errors={screeningErrors}
                    onChange={handleScreeningChange}
                    disabled={isProcessing || !!clientSecret}
                  />
                </div>
              )}

              {/* Security deposit consent grouped with the customer's other
                  required acknowledgments (e.g. SMS opt-in inside the form). */}
              {depositConsentRequired && (
                <div className="space-y-4 pt-2 border-t border-gray-100">
                  <SecurityDepositBanner
                    amount={depositAmount}
                    currency={pricingBreakdown?.currency ?? "USD"}
                    mode={securityDepositMode!}
                    acceptCash={acceptCashDeposit}
                    creditCardRequired={creditCardRequired}
                  />
                  <DepositConsentCheckbox
                    amount={depositAmount}
                    currency={pricingBreakdown?.currency ?? "USD"}
                    mode={securityDepositMode!}
                    acceptCash={acceptCashDeposit}
                    checked={depositConsent}
                    onChange={setDepositConsent}
                    creditCardRequired={creditCardRequired}
                  />
                </div>
              )}
            </div>

            {driversRequired && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <RenterDriverCards
                  state={renterDrivers}
                  returnDate={to}
                  reason={
                    insurerChecksDrivers ? "insurer_check" : "fleet_setting"
                  }
                  disabled={isProcessing || !!clientSecret}
                />
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Payment Information
              </h2>

              {configError?.code === "no_merchant_account" ? (
                <div className="mt-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                    />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">
                    Payment Methods Unavailable
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Payment processing is not configured for this rental.
                  </p>
                </div>
              ) : rentalReady && requiresPostPayDepositHold ? (
                <DepositHoldSection
                  stripePromise={stripePromise}
                  depositAmount={depositAmount}
                  depositStage={depositStage}
                  depositErrorMessage={depositErrorMessage}
                  depositRetryCount={depositRetryCount}
                  depositScheduledAt={depositScheduledAt ?? null}
                  onRetry={retryDeposit}
                  onCardCaptured={(pmId) => {
                    setDepositPaymentMethodId(pmId);
                    void requestDepositHold(pmId);
                  }}
                  onContinue={() => finishCheckout(rentalReady.rentalId)}
                />
              ) : isReturningFromStripe ? (
                <div className="mt-6 flex items-center justify-center min-h-[200px]">
                  <div className="text-center">
                    <div className="animate-spin h-10 w-10 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-700 font-medium">
                      Finalizing your reservation…
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      Please do not close this window.
                    </p>
                  </div>
                </div>
              ) : clientSecret &&
                isCustomerFormValid &&
                !isCalculatingPricing &&
                !isCalculatingDistance &&
                !pricingError ? (
                <div className="mt-6">
                  <Elements
                    stripe={stripePromise}
                    options={{
                      clientSecret: clientSecret || undefined,
                      appearance: { theme: "stripe" },
                      loader: "auto",
                      paymentMethodCreation: requiresPostPayDepositHold
                        ? "manual"
                        : undefined,
                    }}
                    key={
                      requiresPostPayDepositHold
                        ? "deposit-mode"
                        : "default-mode"
                    }
                  >
                    <CheckoutPaymentElementForm
                      returnUrl={(() => {
                        if (typeof window === "undefined") return "";
                        const hold = getPendingSoftHold();
                        if (!hold) return window.location.href;
                        const params = new URLSearchParams({
                          hold_id: hold.hold_id,
                          hold_token: hold.hold_token,
                          api_key: apiKey || "",
                          fleet_id: fleetId || "",
                        });
                        return `${window.location.origin}/embed/checkout/finalize?${params.toString()}`;
                      })()}
                      clientSecret={clientSecret}
                      requireCreditCard={requiresPostPayDepositHold}
                      blockOnNonCredit={false}
                      onPaymentMethodValidated={(pmId) => {
                        rentalPaymentMethodIdRef.current = pmId;
                      }}
                      onSuccess={handlePaymentSuccess}
                      onError={(msg: string) => handlePaymentFailure(msg)}
                      submitButtonText={checkoutCopy.submitLabel}
                    />
                    {checkoutCopy.notice && (
                      <p className="mt-3 text-sm text-gray-600">
                        {checkoutCopy.notice}
                      </p>
                    )}
                  </Elements>
                </div>
              ) : (
                <div className="mt-6">
                  <Button
                    onClick={initializeCheckout}
                    disabled={
                      !isCustomerFormValid ||
                      !!pricingError ||
                      !!configError ||
                      isCalculatingPricing ||
                      isProcessing ||
                      hasUnacceptedInsuranceTerms ||
                      hasUnacceptedMandatoryDocuments ||
                      !depositConsentSatisfied ||
                      driversBlockContinue ||
                      !deliveryModeKnown
                    }
                    className="w-full"
                    size="lg"
                  >
                    {isProcessing
                      ? "Initializing Payment..."
                      : hasUnacceptedMandatoryDocuments
                        ? "Accept the rental documents to continue"
                        : hasUnacceptedInsuranceTerms
                          ? "Accept insurance terms to continue"
                          : checkoutCopy.continueLabel}
                  </Button>
                  {checkoutCopy.notice && (
                    <p className="mt-3 text-sm text-gray-600">
                      {checkoutCopy.notice}
                    </p>
                  )}
                  {driversBlockContinue && !isProcessing && (
                    <p className="mt-2 text-sm text-amber-700">
                      Save every driver&apos;s license to continue.
                    </p>
                  )}
                  {!depositConsentSatisfied && !isProcessing && (
                    <p className="mt-2 text-sm text-amber-700">
                      Please authorize the security deposit to continue.
                    </p>
                  )}
                </div>
              )}

              {checkoutError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-center">
                    <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-3">
                      <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                    </div>
                    <p className="text-sm text-red-600 font-medium">
                      {typeof checkoutError === "string"
                        ? checkoutError
                        : "An error occurred. Please try again."}
                    </p>
                  </div>
                  <DeliveryErrorActions
                    code={checkoutErrorCode}
                    hasLocations={deliveryLocations.length > 0}
                    onChoosePickup={recoverToPickup}
                    onChooseLocation={recoverToLocations}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Summary */}
          <div className="lg:col-span-1 space-y-6">
            {/* Pricing Breakdown */}
            <PricingBreakdownDisplay
              breakdown={pricingBreakdown}
              isLoading={isCalculatingPricing}
              securityDeposit={
                securityDepositMode &&
                securityDepositMode !== "disabled" &&
                vehicle?.security_deposit
                  ? vehicle.security_deposit
                  : null
              }
            />

            {pricingError && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  {typeof pricingError === "string"
                    ? pricingError
                    : "Failed to calculate pricing. Please try again."}
                </p>
                <DeliveryErrorActions
                  code={pricingErrorCode}
                  hasLocations={deliveryLocations.length > 0}
                  onChoosePickup={recoverToPickup}
                  onChooseLocation={recoverToLocations}
                />
              </div>
            )}

            {/* Original Summary */}
            <CheckoutSummary
              vehicleName={resolveVehicleName(vehicle, vehicleName)}
              from={from}
              to={to}
              adults={adults}
              children={children}
              infants={infants}
              pets={pets}
              totalAmount={
                pricingBreakdown
                  ? parseFloat(pricingBreakdown.total)
                  : totalPrice
              }
              isCalculatingRates={isCalculatingPricing}
              dailyRates={
                pricingBreakdown?.breakdown?.base_rental?.daily_rates || []
              }
            />
          </div>
        </div>
      </div>

      {/* Finalizing Overlay */}
      {isFinalizing && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className="text-xl font-bold text-gray-900">
              Finalizing Reservation...
            </h3>
            <p className="text-gray-600 mt-2">
              Please wait while we confirm your booking.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export function CheckoutContainer(props: CheckoutContainerProps) {
  return (
    <EmbedProvider
      apiKey={props.apiKey}
      fleetId={props.fleetId}
      stripePublishableKey={props.stripePublishableKey}
      theme={props.theme}
      apiBaseUrl={props.apiBaseUrl}
    >
      <CheckoutContainerInner {...props} />
    </EmbedProvider>
  );
}

interface DepositHoldSectionProps {
  stripePromise: Promise<any> | null;
  depositAmount: number;
  depositStage: ReturnType<typeof useDepositHoldFlow>["stage"];
  depositErrorMessage: string | null;
  depositRetryCount: number;
  depositScheduledAt: string | null;
  onRetry: () => void;
  onCardCaptured: (paymentMethodId: string) => void;
  onContinue: () => void;
}

/**
 * Post-pay deposit-hold panel. Renders the status (waiting/succeeded/failed/etc.)
 * and, when the backend reports `needs_card`, an inline credit-only PaymentElement
 * via <DepositCardCapture>. Mirrors the equivalent panel in quote-review.
 */
function DepositHoldSection({
  stripePromise,
  depositAmount,
  depositStage,
  depositErrorMessage,
  depositRetryCount,
  depositScheduledAt,
  onRetry,
  onCardCaptured,
  onContinue,
}: DepositHoldSectionProps) {
  const terminal =
    depositStage === "succeeded" ||
    depositStage === "skipped" ||
    depositStage === "scheduled" ||
    depositStage === "failed";

  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 flex items-start gap-3">
        <div className="text-sm">
          <p className="font-medium text-green-900">Reservation confirmed</p>
          <p className="text-green-800 mt-1">
            Your rental payment was processed successfully.
          </p>
        </div>
      </div>

      <DepositHoldStatus
        stage={depositStage}
        amount={depositAmount}
        errorMessage={depositErrorMessage}
        retryCount={depositRetryCount}
        scheduledAt={depositScheduledAt}
        onRetry={onRetry}
      />

      {depositStage === "needs_card" && stripePromise && (
        <Elements
          stripe={stripePromise}
          options={{
            mode: "setup",
            currency: "usd",
            paymentMethodCreation: "manual",
            paymentMethodTypes: ["card"],
            appearance: { theme: "stripe" },
          }}
        >
          <DepositCardCapture captured={false} onCaptured={onCardCaptured} />
        </Elements>
      )}

      {terminal && (
        <Button onClick={onContinue} className="w-full" size="lg">
          Continue
        </Button>
      )}
    </div>
  );
}
